import { englishUiComponentTranslators } from "@/lib/i18n/ui-component-translations"
/**
 * Delta computation for KPI comparison.
 *
 * Pure functions — no React, no side effects.
 */

import { formatShare, wholePercent, type AnalyticsTranslator } from "@/lib/analytics/format"

export type DeltaDirection = "up" | "down" | "flat"

export type DeltaColor = "green" | "red" | "gray"

/** "percent" is a relative change; "from" is a share's change told by the value it came from. */
export type DeltaUnit = "percent" | "from"

interface DeltaFields {
  /** current − previous; for a share, the difference of the rounded percentages */
  absolute: number
  /** ((current − previous) / previous) × 100, null when previous = 0 or the unit is from */
  percent: number | null
  direction: DeltaDirection
  /** Semantic color for UI rendering */
  color: DeltaColor
}

export type ShareTrend = DeltaFields & { unit: "from"; from: string }

export type DeltaResult = (DeltaFields & { unit: Exclude<DeltaUnit, "from"> }) | ShareTrend

/**
 * Metric kind determines color semantics.
 * - "positive" → up = green, down = red  (revenue, orders, ROI)
 * - "negative" → up = red, down = green  (expense)
 */
export type MetricKind = "positive" | "negative"

/**
 * Compute the delta between a current and previous metric value.
 *
 * Edge cases:
 * - previous = 0  → percent = null (cannot compute growth from zero)
 * - both = 0      → direction = "flat", color = "gray"
 * - current = previous → direction = "flat", color = "gray"
 */
export function computeDelta(
  current: number,
  previous: number,
  kind: MetricKind = "positive",
): DeltaResult {
  const absolute = current - previous

  if (current === previous) {
    return { unit: "percent", absolute: 0, percent: previous === 0 ? null : 0, direction: "flat", color: "gray" }
  }

  const direction: DeltaDirection = absolute > 0 ? "up" : "down"
  const percent = previous === 0 ? null : (absolute / previous) * 100

  return { unit: "percent", absolute, percent, direction, color: trendColor(direction, kind) }
}

/**
 * «up from 18%»: a share's change told by the value it came from, so no second percentage stands beside the share and
 * none reads as a relative change. The rounded percentages are compared, so 40% → 40% never reads as a change.
 */
export function computeShareTrend(current: number, previous: number, kind: MetricKind): ShareTrend {
  const from = formatShare(previous, "")
  const absolute = wholePercent(current) - wholePercent(previous)
  if (absolute === 0) return { unit: "from", absolute: 0, percent: null, direction: "flat", color: "gray", from }

  const direction: DeltaDirection = absolute > 0 ? "up" : "down"
  return { unit: "from", absolute, percent: null, direction, color: trendColor(direction, kind), from }
}

/** «up from 44%», «down from 36%», «same as before». */
export function shareTrendLabel(trend: Pick<ShareTrend, "direction" | "from">, t: AnalyticsTranslator = englishUiComponentTranslators.Analytics): string {
  switch (trend.direction) {
    case "up":
      return t("upFrom", {from: trend.from})
    case "down":
      return t("downFrom", {from: trend.from})
    case "flat":
      return t("sameAsBefore")
    default: {
      const exhaustive: never = trend.direction
      return exhaustive
    }
  }
}

function trendColor(direction: "up" | "down", kind: MetricKind): DeltaColor {
  switch (kind) {
    case "positive":
      return direction === "up" ? "green" : "red"
    case "negative":
      return direction === "up" ? "red" : "green"
    default: {
      const exhaustive: never = kind
      return exhaustive
    }
  }
}

export interface KpiTotals {
  revenue: number
  orders: number
  expense: number
  roi: number
}

export interface KpiDeltas {
  revenue: DeltaResult
  orders: DeltaResult
  expense: DeltaResult
  roi: DeltaResult
}

/**
 * Compute deltas for all 4 KPI metrics at once.
 */
export function computeKpiDeltas(current: KpiTotals, previous: KpiTotals): KpiDeltas {
  return {
    revenue: computeDelta(current.revenue, previous.revenue, "positive"),
    orders: computeDelta(current.orders, previous.orders, "positive"),
    expense: computeDelta(current.expense, previous.expense, "negative"),
    roi: computeDelta(current.roi, previous.roi, "positive"),
  }
}

/**
 * Aggregate ChannelStat-like objects into KPI totals.
 * Accepts anything with { revenue, orders, expense? }.
 */
export function aggregateToTotals(
  channels: ReadonlyArray<{ revenue: number; orders: number; expense?: number }>,
): KpiTotals {
  let revenue = 0
  let orders = 0
  let expense = 0

  for (const ch of channels) {
    revenue += ch.revenue
    orders += ch.orders
    expense += ch.expense ?? 0
  }

  const roi = expense > 0 ? ((revenue - expense) / expense) * 100 : 0

  return { revenue, orders, expense, roi }
}
